// Small, pure helper functions shared across the builder and the live app.
import { getDisplayPrefs } from './displayPrefs';
import { docToText } from '../shared/richDoc';

/**
 * Singularise a plural English label well enough for UI display.
 * Handles the common cases: -ies → -y, -xes/-zes/-sses/-uses → drop -es, other -ses → drop -s, plain -s → drop.
 * Falls back to returning the label unchanged if it looks already singular.
 */
export const singularise = (label = '') => {
  if (!label) return label;
  if (/ies$/i.test(label)) return label.replace(/ies$/i, (m) => m[0] === m[0].toUpperCase() ? 'Y' : 'y');
  // -ses is the ambiguous one: "Statuses"/"Addresses" lose "es", but "Leases"/"Courses"/"Warehouses"/"Expenses" lose only "s".
  // The -es comes off after ss/us/is (not after ou/au: "Houses", "Clauses"); every other -ses plural loses just the s.
  if (/(ou|au)ses$/i.test(label)) return label.slice(0, -1);
  if (/(ss|us|is|x|z|ch|sh)es$/i.test(label)) return label.replace(/es$/i, '');
  if (/ses$/i.test(label)) return label.slice(0, -1);
  if (/s$/i.test(label) && label.length > 1) return label.replace(/s$/i, '');
  return label;
};

/**
 * The apps the customer actually built, i.e. everything except the shared
 * services the platform seeds into every org (`employees`, `fiscal-periods`).
 * `shared_service` is stamped per-app by the backend's list route — see
 * db.is_shared_service_app_id, which explains why it can't be inferred from
 * the id's prefix.
 *
 * This is the real "does this org have an app yet?" signal. `apps.length === 0`
 * is NOT: an org has two seeded apps from the moment it's created, so every
 * first-run check written against the raw list was permanently false
 * (docs/onboarding-ux-design-2026-07.md finding B1).
 */
export const ownApps = (apps = []) => apps.filter((a) => !a.shared_service);
// The customer's real apps: not a shared service and not a sandbox (a throwaway copy, WP-11.1). Used wherever an app is COUNTED or
// auto-opened, so trying a change never makes an empty org look populated or lands someone in a copy.
export const liveApps = (apps = []) => apps.filter((a) => !a.shared_service && !a.sandbox_of);

export const slug = (s) =>
  (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'field';

export const uid = () => Math.random().toString(36).slice(2, 8);

export const uniqueKey = (base, taken) => {
  let k = base, i = 2;
  while (taken.includes(k)) k = `${base}_${i++}`;
  return k;
};

// jsonb (Postgres) does not preserve object key order across save/reload, so field
// display order is tracked explicitly via an entity's `field_order` array. These
// helpers merge that array with whatever keys actually exist on `fields`, so things
// still work for legacy/pasted configs that lack (or have a stale) `field_order`.
export const fieldOrder = (fields, order) => {
  const keys = Object.keys(fields || {});
  const kept = (order || []).filter((k) => keys.includes(k));
  const missing = keys.filter((k) => !kept.includes(k));
  return [...kept, ...missing];
};

export const orderedEntries = (fields, order) =>
  fieldOrder(fields, order).map((k) => [k, fields[k]]).filter(([, s]) => s != null);

// Parses a number/money field's raw text input, tolerating the thousands
// separators and currency symbols a person actually types for money
// ("1,200", "$1,200.50") — a plain `Number(str)` rejects a comma outright
// (Number("1,200") is NaN), which is why these fields moved off
// `type="number"` (whose native validation has the same blind spot, and
// additionally lets an idle scroll-wheel silently change the value) onto
// `type="text" inputMode="decimal"`. Returns NaN for genuinely unparseable
// input, same contract as Number() — callers already branch on that.
//
// The currency marker is whatever a person actually types around an amount,
// not only "$": this once stripped `$` alone, so "S$1,200", "RM50",
// "SGD 1,200" and "€50" were all NaN — and NaN goes over the wire as `null`,
// so a Singapore business's everyday way of typing a price saved the field
// EMPTY (audit/findings/first-look-discoverability-2026-09.md §11). Accepted:
// any Unicode currency symbol, optionally after a 1–3 letter prefix ("S$",
// "HK$", "US$"); a bare 2–3 letter prefix ("RM", "SGD"); or a trailing
// symbol or upper-case ISO code ("50€", "1,200 SGD").
//
// Commas must group thousands properly ("1,200", "1,200,000.50"). Anything
// else is refused rather than guessed: stripping every comma turned a
// European "1,5" into 15 and "1.200,50" into 1.2005 — silently wrong
// numbers, worse than a refusal. "10k" and "1e3" are refused for the same
// reason (a guess at what the person meant). A refusal is not silent either:
// `numericInputProblem` below is how every save path says so.
const LEAD_CURRENCY = /^(?:[A-Za-z]{0,3}\p{Sc}|[A-Za-z]{2,3}(?=\s*[\d.]))/u;
const TRAIL_CURRENCY = /(?:\p{Sc}|\s[A-Z]{3}|(?<=\d)[A-Z]{3})$/u;
const PLAIN_AMOUNT = /^(?:\d{1,3}(?:,\d{3})+|\d+)?(?:\.\d*)?$/;

export const parseNumericInput = (raw) => {
  if (raw === '' || raw == null) return NaN;
  if (typeof raw === 'number') return raw;
  let s = String(raw).trim();
  let negative = false;
  const takeSign = () => {
    const m = s.match(/^[-+−]\s*/);
    if (m) { negative = negative || m[0][0] !== '+'; s = s.slice(m[0].length); }
  };
  takeSign();
  s = s.replace(LEAD_CURRENCY, '').trim();
  takeSign(); // "$-50" as well as "-$50"
  s = s.replace(TRAIL_CURRENCY, '').replace(/\s+/g, '');
  if (!/\d/.test(s) || !PLAIN_AMOUNT.test(s)) return NaN;
  const n = Number(s.replace(/,/g, ''));
  return negative ? -n : n;
};

// The one sentence every save path shows for a number it can't read, or null
// when the value is fine (blank counts as fine — "required" is its own
// check). Shared so a record form, an inline cell and a board card never
// disagree about what a readable number is.
export const numericInputProblem = (raw) => {
  if (raw === '' || raw == null) return null;
  return Number.isNaN(parseNumericInput(raw))
    ? "That isn't a number we can read — try something like 1200.50."
    : null;
};

export const fmtNumber = (value, fmt = {}) => {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  const dec = fmt.decimals ?? 0;
  const { locale, currency } = getDisplayPrefs();
  if (fmt.style === 'currency') {
    return new Intl.NumberFormat(locale || undefined, {
      style: 'currency', currency: fmt.currency || currency,
      minimumFractionDigits: dec, maximumFractionDigits: dec,
    }).format(n);
  }
  if (fmt.style === 'percent') return `${n.toLocaleString(locale || undefined, { maximumFractionDigits: dec })}%`;
  return n.toLocaleString(locale || undefined, { maximumFractionDigits: dec });
};

// Money formatter for the accounting views (ledger/trial-balance/intercompany/
// balance-sheet). The backend converts every line into the app's functional
// currency and returns that ISO code as `functional_currency`; render with THAT
// code, not a hardcoded 'USD', so a non-USD functional currency (e.g. JPY) does
// not render a `$` sign on a JPY-magnitude number and mislead an approver by
// ~two orders of magnitude. `functional_currency` is null when no multi-currency
// conversion was configured, in which case USD (the platform default) is correct.
// Bytes as a person reads them: "1 GB", "200 MB", "1.5 KB". Steps of 1024,
// because the platform's storage caps are binary (billing.py's PLAN_CAPS:
// 200 * 1024 ** 2, 1 * 1024 ** 3), so a 1 GB cap reads "1 GB" rather than
// "1.07 GB". At most one decimal, and none on a whole number.
export const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined || bytes === '') return '';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = n;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} ${units[i]}`;
};

export const fmtMoney = (value, currency) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  const prefs = getDisplayPrefs();
  return new Intl.NumberFormat(prefs.locale || undefined, {
    style: 'currency', currency: currency || prefs.currency,
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
};

// Tailwind text-color class for a single line-item cell: red when negative,
// neutral otherwise. Never green here — a plain account line going positive
// isn't an achievement the way a bottom-line total is, so only `finalMoneyTone`
// (subtotal/total rows) rewards a positive sign with green.
export const moneyTone = (value) =>
  Number(value) < 0 ? 'text-rose-700 dark:text-rose-300' : 'text-neutral-700 dark:text-neutral-200';

// Tailwind text-color class for a subtotal/total row: green at/above zero,
// red below. Matches the sign convention every statement's bottom line
// already used before this was extracted (income statement's Net Income,
// balance sheet's inherited-from-subtotal rows, cash flow's Net Change in Cash).
export const finalMoneyTone = (value) =>
  Number(value) >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300';

// --- row-action conditions ---------------------------------------------------
// Mirror of engine.eval_action_condition (backend). Decides whether a row action
// is offered for a given row. Accepts a single rule {field, op, value} or a group
// {match: 'all'|'any', rules: [...]}. Kept deliberately in sync with the server,
// which re-checks the same logic as the real gate.

const truthyEq = (a, b) => {
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b);
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull || bNull) return aNull && bNull;
  const na = Number(a), nb = Number(b);
  if (a !== '' && b !== '' && !Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return String(a) === String(b);
};

const evalActionRule = (rule, row) => {
  if (!rule || typeof rule !== 'object') return true;
  const actual = (row || {})[rule.field];
  const target = rule.value;
  const empty = actual === null || actual === undefined || actual === ''
    || (Array.isArray(actual) && actual.length === 0);
  switch (rule.op || 'eq') {
    case 'empty': return empty;
    case 'not_empty': return !empty;
    case 'eq': return truthyEq(actual, target);
    case 'ne': return !truthyEq(actual, target);
    case 'gt': case 'gte': case 'lt': case 'lte': {
      const l = Number(actual), r = Number(target);
      if (Number.isNaN(l) || Number.isNaN(r)) return false;
      if (rule.op === 'gt') return l > r;
      if (rule.op === 'gte') return l >= r;
      if (rule.op === 'lt') return l < r;
      return l <= r;
    }
    case 'in': return Array.isArray(target) && target.includes(actual);
    case 'not_in': return !(Array.isArray(target) && target.includes(actual));
    case 'contains': return String(actual ?? '').includes(String(target ?? ''));
    default: return true;
  }
};

export const evalActionCondition = (condition, row) => {
  if (!condition) return true;
  if (Array.isArray(condition.rules)) {
    if (!condition.rules.length) return true;
    const res = condition.rules.map((r) => evalActionRule(r, row));
    return String(condition.match || 'all').toLowerCase() === 'any' ? res.some(Boolean) : res.every(Boolean);
  }
  return evalActionRule(condition, row);
};

// Operators offered in the builder's condition UI (a subset; the evaluator
// above understands more). Labels are human-facing; ids match the evaluator.
export const CONDITION_OPS = [
  { id: 'eq', label: 'is' },
  { id: 'ne', label: 'is not' },
  { id: 'gt', label: '>' },
  { id: 'gte', label: '≥' },
  { id: 'lt', label: '<' },
  { id: 'lte', label: '≤' },
  { id: 'empty', label: 'is empty' },
  { id: 'not_empty', label: 'is not empty' },
];

// --- computed field classification (mirror of engine.is_virtual et al.) ------
// A field is computed when it has a `compute` block. A rollup or a formula is
// virtual (resolved on read, no column) unless it opts into being stored
// (compute.persisted), which gives it a real column so it can be summed,
// sorted and filtered in SQL like any other field — a persisted rollup is
// kept correct by the backend recomputing it whenever a matching child row
// is written (docs/recipes-templates-domains-plan-2026-07.md Sprint 5), not
// by anything in this file.

export const isComputed = (spec) => !!spec?.compute;

export const isVirtualField = (spec) => {
  const c = spec?.compute;
  if (!c) return false;
  if (spec.virtual) return true;
  if (c.kind === 'rollup' || c.kind === 'formula') return !c.persisted;
  return false;
};

// Whether FilterBar can offer this field, and whether SQL can honour it.
// One predicate rather than two lists, because TableView's per-column
// "filter by this column" affordance and FilterBar's own field picker must
// agree - a header offering a filter the panel then refuses to show is
// worse than not offering it.
//
// Virtual computed fields are out (no column to filter on - dataset.py
// raises on them). `readonly` is NOT a reason to exclude: it governs
// whether a person may type into the field, not whether SQL can compare it,
// and every persisted rollup or formula is readonly by construction.
export const FILTERABLE_TYPES = ['string', 'number', 'money', 'rating', 'enum', 'boolean', 'datetime', 'reference'];

// `sensitive` fields are excluded too, and that is a correctness rule rather
// than a tidiness one: the column holds a randomised AES-GCM envelope, so
// there is no value a filter over it could ever match, and the backend now
// REFUSES such a filter outright (db/records.py's `_filter_column`) rather
// than dropping it and quietly returning every row. Offering the control
// would hand someone a 400 for using the UI as designed.
export const isFilterableField = (spec) =>
  !!spec && !isVirtualField(spec) && !spec.hide_in_list && !spec.sensitive
  && FILTERABLE_TYPES.includes(spec.type);

// Field pickers for views (filters, search, dimensions, measures) only offer
// fields that exist as real columns — virtual computed fields (rollups,
// on-read formulas) can't be aggregated/filtered/searched in SQL, so they're
// excluded. So are `sensitive` fields, for the same reason isFilterableField
// excludes them: a filter, search, dimension or measure over ciphertext is a
// question SQL cannot answer, and the backend refuses it rather than
// answering it wrongly.
export function selectFieldsByType(config, entityKey, types) {
  return Object.entries(config?.entities?.[entityKey]?.fields || {})
    .filter(([, s]) => types.includes(s.type) && !isVirtualField(s) && !s.sensitive)
    .map(([k, s]) => ({ key: k, label: s.label }));
}

// --- dashboard block layout (12-column grid geometry) ------------------------
// Blocks place into a 12-col CSS grid. `colspan` (1-12) is the width; the legacy
// `width` ('third'|'half'|'full') maps onto it so existing dashboards are
// unchanged. `rowspan` (default 1) reserves vertical tracks; optional `col`/`row`
// pin a block to an explicit grid line (advanced; auto-flow otherwise).
export const WIDTH_TO_COLSPAN = { third: 4, half: 6, full: 12 };

export const blockColspan = (b) => {
  const n = Number(b?.colspan ?? WIDTH_TO_COLSPAN[b?.width] ?? 12);
  return Math.min(12, Math.max(1, Math.round(n) || 12));
};

export const blockRowspan = (b) => {
  const n = Number(b?.rowspan ?? 1);
  return Math.min(6, Math.max(1, Math.round(n) || 1));
};

// Inline grid style for a block. Spans clamp to the available columns, so on a
// single-column (mobile) grid every block stacks full-width automatically.
export const blockGridStyle = (b) => {
  const cs = blockColspan(b);
  const rs = blockRowspan(b);
  const col = Number(b?.col);
  const row = Number(b?.row);
  return {
    gridColumn: col >= 1 ? `${Math.min(12, Math.round(col))} / span ${cs}` : `span ${cs}`,
    gridRow: row >= 1 ? `${Math.round(row)} / span ${rs}` : `span ${rs}`,
  };
};

// Width presets offered in the builder (label -> colspan on the 12-col grid).
export const COLSPAN_PRESETS = [
  { label: '¼', span: 3 },
  { label: '⅓', span: 4 },
  { label: '½', span: 6 },
  { label: '⅔', span: 8 },
  { label: '¾', span: 9 },
  { label: 'full', span: 12 },
];
export const ROWSPAN_PRESETS = [1, 2, 3];

// --- row coloring (config `row_colors`) --------------------------------------
// Shared by every live view that supports conditional row/card tinting, so a
// rule authored once behaves identically everywhere it's evaluated (this used
// to be reimplemented per-view — e.g. SplitView had its own weaker eq/ne-only
// copy that TableView's richer version didn't feed into).
// Rule shape: { condition: { field, op, value }, color: 'rose'|'amber'|'emerald'|'blue'|'violet'|'cyan' }
export const ROW_COLOR_MAP = {
  rose:    'bg-rose-50 dark:bg-rose-950/20',
  amber:   'bg-amber-50 dark:bg-amber-950/20',
  emerald: 'bg-emerald-50 dark:bg-emerald-950/20',
  blue:    'bg-blue-50 dark:bg-blue-950/20',
  violet:  'bg-violet-50 dark:bg-violet-950/20',
  cyan:    'bg-cyan-50 dark:bg-cyan-950/20',
};
// Returns the matching rule's raw color name ('rose'|'amber'|...), or null —
// for callers (like SplitView) that need to map the color onto their own
// class set (e.g. a border-left accent) rather than TableView's plain bg tint.
export function matchRowColor(row, rowColors) {
  if (!rowColors?.length) return null;
  for (const rule of rowColors) {
    const c = rule.condition;
    if (!c?.field) continue;
    const actual = row[c.field];
    let match = false;
    switch (c.op || 'eq') {
      case 'eq':        match = String(actual ?? '') === String(c.value ?? ''); break;
      case 'ne':        match = String(actual ?? '') !== String(c.value ?? ''); break;
      case 'empty':     match = actual === null || actual === undefined || actual === ''; break;
      case 'not_empty': match = actual !== null && actual !== undefined && actual !== ''; break;
      case 'gt':        match = Number(actual) > Number(c.value); break;
      case 'lt':        match = Number(actual) < Number(c.value); break;
      default:          break;
    }
    if (match) return rule.color || null;
  }
  return null;
}

export function evalRowColor(row, rowColors) {
  const color = matchRowColor(row, rowColors);
  return color ? (ROW_COLOR_MAP[color] || null) : null;
}

// --- server-side query building (FilterBar output -> entityQuery body) ------
// Shared by every live view that lists rows through the dataset engine
// (`/api/apps/:id/query/:entity`, see api.js's entityQuery), so filtering,
// search, sort and pagination behave identically everywhere instead of each
// view growing its own (previously TableView-only) coerce/where-builder.
// FilterBar emits { simpleFilters, rules, match } - `rules` is the one that
// matters here (simpleFilters exists only for SavedViewsBar compatibility).
const coerceFilterValue = (spec, v) => {
  if (spec?.type === 'number' || spec?.type === 'money') return v === '' ? null : Number(v);
  if (spec?.type === 'boolean') return v === 'true' || v === true;
  return v;
};

// Relative-date filter ops (FilterBar's datetime operator list) — computed
// client-side into a concrete [gte, lt) range at query-build time (not
// baked in at selection time), so "today"/"this week" stay correct across
// a saved view reloaded on a different day. Local-time day boundaries, same
// precision the literal `is`/`after`/`before` date filters already have.
export const RELATIVE_DATE_OPS = [
  'today', 'this_week', 'this_month', 'this_quarter', 'last_n_days', 'next_n_days', 'overdue',
];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function startOfWeek(d) { const x = startOfDay(d); const day = x.getDay(); x.setDate(x.getDate() + (day === 0 ? -6 : 1 - day)); return x; }
function isoDate(d) { return d.toISOString().slice(0, 10); }

// `overdue` has no lower bound (any date before today, however far back), so
// it returns `{ lt }` alone rather than the `{ gte, lt }` shape every other
// op returns — `buildEntityQuery` below only sets `gte` when the range
// actually has one.
export function relativeDateRange(op, n) {
  const now = new Date();
  let start, end;
  if (op === 'today') {
    start = startOfDay(now); end = new Date(start); end.setDate(end.getDate() + 1);
  } else if (op === 'this_week') {
    start = startOfWeek(now); end = new Date(start); end.setDate(end.getDate() + 7);
  } else if (op === 'this_month') {
    start = new Date(now.getFullYear(), now.getMonth(), 1);
    end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  } else if (op === 'this_quarter') {
    const q = Math.floor(now.getMonth() / 3);
    start = new Date(now.getFullYear(), q * 3, 1);
    end = new Date(now.getFullYear(), q * 3 + 3, 1);
  } else if (op === 'last_n_days') {
    end = startOfDay(now); end.setDate(end.getDate() + 1);
    start = new Date(end); start.setDate(start.getDate() - (Number(n) > 0 ? Number(n) : 7));
  } else if (op === 'next_n_days') {
    // Symmetric with last_n_days: today is the first of the N days, so an
    // N-day "last" window and an N-day "next" window both include today
    // exactly once between them rather than double-counting or skipping it.
    start = startOfDay(now);
    end = new Date(start); end.setDate(end.getDate() + (Number(n) > 0 ? Number(n) : 7));
  } else if (op === 'overdue') {
    return { lt: isoDate(startOfDay(now)) };
  } else {
    return null;
  }
  return { gte: isoDate(start), lt: isoDate(end) };
}

export function buildEntityQuery({ entityCfg, advancedFilters, search, searchFields, sort, limit, offset }) {
  const where = {};
  const whereAny = {};
  const put = (bag, field, op, operand) => { bag[field] = { ...(bag[field] || {}), [op]: operand }; };
  const { rules = [], match = 'all' } = advancedFilters || {};
  const bag = match === 'any' && rules.length > 1 ? whereAny : where;
  for (const r of rules) {
    if (!r.field) continue;
    const spec = entityCfg?.fields?.[r.field];
    if (r.op === 'empty' || r.op === 'not_empty') put(bag, r.field, r.op, true);
    else if (RELATIVE_DATE_OPS.includes(r.op)) {
      const range = relativeDateRange(r.op, r.value);
      if (range) {
        if (range.gte !== undefined) put(bag, r.field, 'gte', range.gte);
        put(bag, r.field, 'lt', range.lt);
      }
    }
    else if (r.op === 'in' || r.op === 'not_in') {
      // A list op carries an array (FilterBar's multi-value editor); a bare scalar from a hand-written default is one member.
      const list = (Array.isArray(r.value) ? r.value : [r.value]).filter((v) => v !== '' && v !== null && v !== undefined);
      if (list.length) put(bag, r.field, r.op, list.map((v) => coerceFilterValue(spec, v)));
    }
    else if (r.value !== '') put(bag, r.field, r.op || 'eq', coerceFilterValue(spec, r.value));
  }
  return {
    where,
    where_any: whereAny,
    search: search || '',
    search_fields: searchFields || [],
    // Most callers still pass a single {field, dir} object (one-column
    // sort); TableView's multi-column sort passes an array directly - both
    // compile to the same order[] shape the backend already accepts.
    order: Array.isArray(sort) ? sort : (sort ? [sort] : []),
    limit,
    offset: offset || 0,
  };
}

export const formatCell = (value, spec) => {
  if (value === null || value === undefined || value === '') return '—';
  if (spec?.type === 'richtext') return docToText(value) || '—';
  // A signature is the server's public form; as text it is the same sentence an export prints.
  if (spec?.type === 'signature') return (value && typeof value === 'object' && value.text) || '—';
  if (spec?.type === 'number') return fmtNumber(value, spec.format);
  // No currency default here - fmtNumber's own fallback is the org's
  // configured display currency (getDisplayPrefs), not a hardcoded 'USD';
  // spec.format.currency (a field-level override) still wins over that.
  // 2 dp, not the column's full `numeric(20,4)` precision — the storage
  // precision is deliberately wider than the display default (that extra
  // precision is why the spec prefers `money` over `number` for real amounts),
  // but defaulting the DISPLAY to 4 dp rendered every unformatted money field
  // as `$10.0000`. `format.decimals` overrides, up to the column's 4, and the
  // builder can now actually set it (builder/FieldRow.jsx).
  if (spec?.type === 'money') return fmtNumber(value, { style: 'currency', decimals: 2, ...spec.format });
  if (spec?.type === 'datetime') return fmtDateTime(value, !spec.include_time);
  // Yes/No, matching what `shared/Cellv.jsx` renders for the same field. The
  // two shared renderers disagreeing meant a boolean read "true" wherever
  // formatCell is the one in play (a data-proposal preview, a dashboard
  // tile) and "Yes" in the table beside it — for the same column.
  if (spec?.type === 'boolean') return value ? 'Yes' : 'No';
  if (spec?.type === 'rating') {
    const scale = spec.scale || 5;
    const n = Math.max(0, Math.min(scale, Number(value) || 0));
    return '★'.repeat(n) + '☆'.repeat(scale - n);
  }
  return String(value);
};

// The singular noun for one record of an entity — "Add invoice", "New Deal",
// "3 Tickets" vs "1 Ticket".
//
// `singularise` is a four-rule regex and it is wrong in a way no regex can
// fix: it cannot tell a plural from a singular that happens to end in -s.
// Real labels a customer would plausibly type:
//
//     Address -> "Addres"      Status  -> "Statu"     Campus -> "Campu"
//     News    -> "New"         Bonus   -> "Bonu"      Series -> "Sery"
//     People  -> "People"      Analyses -> "Analys"
//
// So "Add addres" on the primary button of a live app. No shipped config
// hits this today — all 50 use regular plurals — which makes it latent, not
// theoretical: "Address" is an ordinary thing to call an entity.
//
// `label_singular` lets the author say it outright. It needs no backend
// change: `db.platform_apps.save_app` stores the config document verbatim
// as jsonb (`Json(config)`) and the unknown-key checks are deliberately
// narrow (`appdata/rule_shape_rules.py`), so the key round-trips a save
// untouched. The regex stays as the fallback for every entity that doesn't
// need it, which is most of them.
// A list named for the collection rather than its items ("Chart of Accounts",
// "Register of Assets") holds the thing after "of": its rows are accounts, so
// "Add your first chart of account" became "Add your first account". Not
// "Bill of Materials": a row of that is a bill.
const COLLECTION_OF = /^(?:chart|list|register|catalog(?:ue)?|directory|index)s? of (.+)$/i;

export const entityNoun = (entityCfg) => {
  const explicit = entityCfg?.label_singular;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
  const label = entityCfg?.label || '';
  const of = label.match(COLLECTION_OF);
  if (of) {
    const item = singularise(of[1]);
    return label[0] === label[0].toUpperCase() ? item.charAt(0).toUpperCase() + item.slice(1) : item;
  }
  return singularise(label);
};

// A two-letter stand-in for a view's name, for the collapsed nav rail.
//
// The rail's collapsed icon comes from the view TYPE, so an app with several
// views of one type renders that many identical icons in a vertical stack —
// and that is the normal case, not an edge one: 29 of the 50 configs shipped
// in kesher-templates/ and keshersuite-apps/ have three or more views of a
// single type, topping out at thirteen. A `title` tooltip is the only thing
// telling them apart, which means hovering each one in turn, and nothing at
// all on a touch device.
//
// Initials of the first two words, else the first two letters. Collisions
// are possible and fine — the job is to break up a wall of identical icons,
// not to be a unique key.
export const viewInitials = (title) => {
  const words = String(title || '').trim().split(/[\s\-_/]+/).filter(Boolean);
  if (!words.length) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// The one way a non-table view turns a row's field into display text — a
// card subtitle, a calendar chip, a Gantt bar caption, a graph node label.
// Those six views each derived it by hand as `row[f + '__label'] || row[f]`
// stringified, which had two problems `TableView`/`SplitView` don't, because
// they render through `Cellv`:
//
//   1. No formatting. An amount read `1250` on the card and `$1,250.00` in
//      the table; a timestamp read `2026-08-01T00:00:00Z` next to
//      `Aug 1, 2026`. Same column, same row, two answers.
//   2. `||` treats a legitimate `0` or `false` as absent, so a quantity of
//      zero rendered as an empty caption rather than as "0".
//
// Returns '' for genuinely empty rather than `formatCell`'s '—' placeholder,
// leaving each caller to decide whether nothing deserves space on screen.
export const rowDisplayText = (row, field, spec) => {
  if (!field) return '';
  // A reference keeps its server-resolved label: formatCell has no way to
  // know the uuid in the column names a row somewhere else.
  const labelled = row?.[`${field}__label`];
  if (labelled) return String(labelled);
  const v = row?.[field];
  if (v === null || v === undefined || v === '') return '';
  return String(formatCell(v, spec));
};

// Org-wide date/time formatting (enterprise-polish item 10): every timestamp
// renders in the org's configured timezone/locale instead of whatever the
// viewer's own browser happens to be set to - two people in different
// timezones looking at the same row used to see two different clock times
// with no indication either way. dateOnly=true drops the time-of-day part
// (list/detail views that only ever show a date, e.g. LedgerView's "Date"
// column) while still resolving through the same configured timezone, so a
// date near midnight doesn't shift a day depending on the viewer's own zone.
export const fmtDateTime = (value, dateOnly = false) => {
  if (value === null || value === undefined || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const { locale, timezone } = getDisplayPrefs();
  // A datetime at exactly 00:00:00 UTC is a DATE kept in a datetime field (the accounting suite's invoice, bill
  // and payment dates are): "Oct 10, 2026, 12:00 AM" on a bill reads as an error, and in a zone west of UTC the
  // org-timezone render would even show the day before. Print it as the date it is, in UTC (browser-pass F11).
  if (!dateOnly && d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0) {
    return d.toLocaleString(locale || undefined, { year: 'numeric', month: 'short', day: '2-digit', timeZone: 'UTC' });
  }
  const opts = dateOnly
    ? { year: 'numeric', month: 'short', day: '2-digit', timeZone: timezone }
    : { year: 'numeric', month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', timeZone: timezone, timeZoneName: 'short' };
  return d.toLocaleString(locale || undefined, opts);
};

// A clock time that always names its zone ("Oct 3, 2026, 9:05 AM GMT+8"): the org's zone when one is set, the
// viewer's own otherwise - either way the zone is printed, so nobody has to guess what time is meant. `timeOnly`
// drops the date (a "until 3:40 PM SGT" sentence).
export const fmtZoned = (value, { timeOnly = false } = {}) => {
  if (value === null || value === undefined || value === '') return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const { locale, timezone } = getDisplayPrefs();
  const opts = timeOnly
    ? { hour: 'numeric', minute: '2-digit', timeZone: timezone, timeZoneName: 'short' }
    : { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: timezone, timeZoneName: 'short' };
  return d.toLocaleString(locale || undefined, opts);
};

// The label to render for one row of the aggregate op's `buckets` — the
// resolved name of a reference group, or the raw group value for everything
// else. Shared by every consumer of that op, because the failure it exists to
// stop is the same one in each: a reference column holds a uuid, so "sales by
// account" grouped on `account_id` came back as a column of uuids and both
// ChartView (on an axis) and PivotView (down the side of a grid) rendered
// them literally. db/records.py's `_agg_label_join` is the server half.
export const BUCKET_BLANK_LABEL = '(blank)';

export const bucketLabel = (bucket, key = 'bucket') => {
  const labelKey = `${key}__label`;
  // The KEY's presence is the signal, not the value's truthiness: it is there
  // only when the server resolved a reference, and in that case the raw group
  // value is a uuid. So an empty label reads as blank rather than falling
  // back — a uuid is not a more informative answer than nothing.
  if (bucket && labelKey in bucket) {
    const label = bucket[labelKey];
    return label == null || label === '' ? BUCKET_BLANK_LABEL : String(label);
  }
  const raw = bucket?.[key];
  // '(none)' is what the aggregate op COALESCEs a null group to.
  return raw == null || raw === '(none)' ? BUCKET_BLANK_LABEL : String(raw);
};
